import React, { useEffect, useMemo, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput,
  fmtDate, panelPost, useAdminGet,
} from '../kit';

/**
 * Ocean · Search engine: OCEAN's own search, run the way COSMOS's is
 * (Backend/ocean/engine.py), panel section "ocean_search"
 * (Backend/ocean/search_admin.py).
 *
 * Dataset: which companies the serving index holds — every company, or only
 * companies with at most N statements (exact-unique by default: the initial
 * OCEAN dataset) — with live counts and a rebuild of the serving index and the
 * keyword index with the saved choice.
 * Indexes: the keyword index in Typesense (ocean/ts_index.py: typos, plurals,
 * filters) and the semantic index (ocean/vectors.py: one bge-small vector per
 * statement, used by the AI route and by the cards' similar lists), each
 * against the serving index's statements, with build buttons that run in the
 * background and show progress.
 * Settings: route (auto / keyword / AI), how many results are held, the AI
 * route's thresholds, and the cards' default scope (same industry / all).
 * Test search: one search with its route, legs and timings.
 * Recent member searches with route, counts and time.
 */

const S = 'ocean_search';
const num = (v: any) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

const LABELS: Record<string, string> = {
  route_mode: 'Route', classify_llm: 'Ask the model about 3-word searches', keyword_engine: 'Keyword engine',
  held_n: 'Results held (keyword)', typo_tolerance: 'Typos allowed per word', ai_enabled: 'AI search',
  ai_max: 'Results kept (AI)', ai_min_relevance: 'Lowest relevance kept (AI)', ai_candidates: 'Candidates the AI scores',
  ai_vector_n: 'Semantic candidates per query', ai_expand: 'Query expansion (AI)', rescore_chunk: 'Statements per AI call',
  rescore_concurrency: 'AI calls at once', nonsense_check: '"Didn\'t look like a topic" check',
  detect_countries: 'Country from the search words', similar_scope: 'Cards: similar lists within',
  similar_engine: 'Cards: similar lists by', cache_ttl_s: 'Keep results for (seconds)',
  card_scope_mode: 'Cards: results shown',
};
const CHOICE_LABELS: Record<string, Record<string, string>> = {
  route_mode: { auto: 'Auto (short → keyword, long → AI)', keyword: 'Keyword only', ai: 'AI only' },
  keyword_engine: { typesense: 'Typesense', sqlite: 'SQLite full text' },
  similar_scope: { industry: 'The same industry', all: 'All industries' },
  similar_engine: { vectors: 'Meaning (semantic index)', keyword: 'Shared words' },
  card_scope_mode: { both: 'Both, with a switch', industry_only: 'Same industry only', all_only: 'All industries only' },
};
const ORDER = ['route_mode', 'ai_enabled', 'classify_llm', 'keyword_engine', 'held_n', 'typo_tolerance',
  'ai_max', 'ai_min_relevance', 'ai_candidates', 'ai_vector_n', 'ai_expand', 'rescore_chunk', 'rescore_concurrency',
  'nonsense_check', 'detect_countries', 'card_scope_mode', 'similar_scope', 'similar_engine', 'cache_ttl_s'];

const DATASET_KEYS = ['dataset_scope', 'dataset_cap_metric', 'dataset_cap_max', 'dataset_country_from_domain'];
const METRIC_LABELS: Record<string, string> = {
  exact_unique: 'statements, exact duplicates removed',
  unique_problems: 'statements, rewordings merged too',
  raw_statements: 'statements found (every extraction)',
};

const Bar: React.FC<{ done: number; total: number | null }> = ({ done, total }) => {
  const pct = total ? Math.min(100, Math.round((100 * done) / total)) : 0;
  return (
    <div className="flex items-center gap-2 text-xs text-grayscale-1100">
      <div className="h-2 w-48 rounded-full bg-grayscale-300 overflow-hidden">
        <div className="h-2 bg-primary-500" style={{ width: `${pct}%` }} />
      </div>
      {total ? `${num(done)} / ${num(total)} (${pct}%)` : 'starting…'}
    </div>
  );
};

const SearchEngine: React.FC = () => {
  const res = useAdminGet<any>(S, 'overview');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState('');
  const [draft, setDraft] = useState<Record<string, any> | null>(null);
  const [tq, setTq] = useState('');
  const [troute, setTroute] = useState('auto');
  const [test, setTest] = useState<any | null>(null);
  const [ds, setDs] = useState<Record<string, any> | null>(null);

  const d = res.data || {};
  const job = d.job || {};
  const live = !!job.running;
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => res.reload(), 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);
  useEffect(() => { if (d.settings && draft === null) setDraft({ ...d.settings }); }, [d.settings, draft]);
  useEffect(() => {
    if (d.settings && ds === null) setDs(Object.fromEntries(DATASET_KEYS.map(k => [k, d.settings[k]])));
  }, [d.settings, ds]);
  const capOk = !!ds && Number.isInteger(ds.dataset_cap_max) && ds.dataset_cap_max >= 1 && ds.dataset_cap_max <= 1000;
  const preview = useAdminGet<any>(S, 'dataset_preview', ds && capOk ? {
    scope: ds.dataset_scope, metric: ds.dataset_cap_metric, cap: ds.dataset_cap_max,
    country_from_domain: ds.dataset_country_from_domain ? 'true' : 'false',
  } : {}, [ds?.dataset_scope, ds?.dataset_cap_metric, ds?.dataset_cap_max, ds?.dataset_country_from_domain]);
  const dsChanged = useMemo(() => {
    if (!ds || !d.settings) return {};
    const out: Record<string, any> = {};
    for (const k of DATASET_KEYS) if (ds[k] !== d.settings[k]) out[k] = ds[k];
    return out;
  }, [ds, d.settings]);

  const act = async (action: string, body: Record<string, any>, done: (r: any) => string) => {
    setBusy(action); setMsg(null);
    const out = await panelPost(S, action, body);
    setBusy('');
    if (out.error) { setMsg({ kind: 'error', text: out.error }); return null; }
    setMsg({ kind: 'ok', text: done(out.data) });
    res.reload();
    return out.data;
  };

  const changed = useMemo(() => {
    if (!draft || !d.settings) return {};
    const out: Record<string, any> = {};
    for (const k of Object.keys(draft)) {
      if (!DATASET_KEYS.includes(k) && draft[k] !== d.settings[k]) out[k] = draft[k];
    }
    return out;
  }, [draft, d.settings]);

  if (res.loading && !res.data) return <Loading />;
  if (res.error) return <Msg kind="error">{res.error}</Msg>;

  const ix = d.index || {};
  const ts = d.typesense || {};
  const vec = d.vectors || {};
  const tsBehind = ts.documents != null && ix.problems != null ? ix.problems - ts.documents : null;
  const vecTodo = (vec.missing || 0) + (vec.stale || 0);
  const stats = d.stats_7d || {};

  const pv = preview.data || {};
  const chosen = pv.chosen || {};
  const rebuild = async () => {
    if (Object.keys(dsChanged).length) {
      setBusy('settings'); setMsg(null);
      const saved = await panelPost(S, 'settings', { settings: dsChanged });
      setBusy('');
      if (saved.error) { setMsg({ kind: 'error', text: saved.error }); return; }
    }
    await act('build', { target: 'serving', mode: 'rebuild' }, () => 'Rebuild started, progress under Indexes.');
  };

  const runTest = async () => {
    setBusy('test'); setTest(null);
    const out = await panelPost(S, 'test', { q: tq, route: troute === 'auto' ? undefined : troute });
    setBusy('');
    setTest(out.error ? { error: out.error } : out.data);
  };

  const field = (k: string) => {
    const v = draft?.[k];
    const set = (x: any) => setDraft(p => ({ ...(p || {}), [k]: x }));
    if (typeof d.defaults?.[k] === 'boolean') {
      return <input type="checkbox" checked={!!v} onChange={e => set(e.target.checked)} />;
    }
    if (d.choices?.[k]) {
      return <Select value={String(v)} onChange={set} options={d.choices[k]} labels={CHOICE_LABELS[k]} />;
    }
    const [lo, hi] = d.ranges?.[k] || [0, 0];
    return (
      <div className="flex items-center gap-2">
        <TextInput type="number" value={String(v ?? '')} onChange={x => set(x === '' ? '' : Number(x))} />
        <span className="text-xs text-grayscale-1100">{lo}–{hi}</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Problem statements" value={num(ix.problems)}
             hint={ix.built_at ? `serving index built ${fmtDate(ix.built_at)} · ${num(ix.companies)} companies` : ix.error} />
        <KPI label="Keyword index (Typesense)" value={ts.healthy ? num(ts.documents) : 'not answering'}
             hint={ts.healthy ? (tsBehind ? `${num(tsBehind)} behind the serving index` : 'in step with the serving index')
               + (ts.server_memory_mb ? ` · Typesense ${num(ts.server_memory_mb)} MB` : '') : (ts.error || 'searches use SQLite full text')} />
        <KPI label="Semantic index" value={num(vec.vectors)}
             hint={vec.vectors ? `${vecTodo ? `${num(vecTodo)} to embed` : 'complete'}${vec.removed ? ` · ${num(vec.removed)} to drop` : ''}`
               + (vec.outside_dataset ? ` · ${num(vec.outside_dataset)} kept outside the dataset` : '')
               + (vec.size_mb ? ` · ${num(vec.size_mb)} MB` : '') : 'not built: AI search uses keyword candidates; cards use shared words'} />
        <KPI label="Searches (7 days)" value={num(Object.values(stats).reduce((a: number, x: any) => a + (x.searches || 0), 0))}
             hint={Object.entries(stats).map(([r, x]: [string, any]) => `${r} ${num(x.searches)} · ${x.avg_ms} ms`).join(' · ') || undefined} />
      </KPIRow>

      <Section title="Dataset used for indexing">
        <p className="text-sm text-grayscale-1200">
          Live index: <span className="font-semibold">{ix.dataset_label || 'every company'}</span>
          {ix.problems != null && ` · ${num(ix.problems)} problem statements from ${num(ix.companies)} companies`}
          {ix.built_at && ` · built ${fmtDate(ix.built_at)}`}
        </p>
        {ds && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap text-sm text-grayscale-1200">
              <Select value={ds.dataset_scope} onChange={v => setDs(p => ({ ...(p || {}), dataset_scope: v }))}
                      options={['capped', 'all']} labels={{ capped: 'Only companies with at most', all: 'Every company' }} />
              {ds.dataset_scope === 'capped' && (
                <>
                  <TextInput type="number" value={String(ds.dataset_cap_max ?? '')}
                             onChange={x => setDs(p => ({ ...(p || {}), dataset_cap_max: x === '' ? '' : Number(x) }))} />
                  <Select value={ds.dataset_cap_metric} onChange={v => setDs(p => ({ ...(p || {}), dataset_cap_metric: v }))}
                          options={Object.keys(METRIC_LABELS)} labels={METRIC_LABELS} />
                </>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-grayscale-1200">
              <input type="checkbox" checked={!!ds.dataset_country_from_domain}
                     onChange={e => setDs(p => ({ ...(p || {}), dataset_country_from_domain: e.target.checked }))} />
              A company that states no HQ country gets its website's country domain (.in India, .co.uk United Kingdom, .de Germany …)
            </label>
            {!capOk && ds.dataset_scope === 'capped' && <Msg kind="error">The number must be between 1 and 1,000.</Msg>}
            {capOk && (preview.error ? <Msg kind="error">{preview.error}</Msg> : (
              <p className="text-sm text-grayscale-1200">
                {preview.loading && !pv.chosen ? 'Counting…' : (
                  <>
                    This choice indexes <span className="font-semibold">{num(chosen.problems)}</span> problem statements
                    from <span className="font-semibold">{num(chosen.companies)}</span> companies
                    {' '}({num(chosen.exact_unique)} exact-unique statements; the whole dataset is {num(pv.all?.problems)} from {num(pv.all?.companies)}).
                    {' '}HQ country: {num(chosen.country_stated)} stated
                    {ds.dataset_country_from_domain ? `, ${num(chosen.country_from_domain)} from the website's domain` : ''},
                    {' '}{num(chosen.no_country)} not stated.
                  </>
                )}
              </p>
            ))}
            <div className="flex gap-2 flex-wrap">
              <Btn small disabled={!Object.keys(dsChanged).length}
                   onClick={() => setDs(Object.fromEntries(DATASET_KEYS.map(k => [k, d.settings[k]])))}>Undo</Btn>
              <Btn small disabled={!Object.keys(dsChanged).length || !capOk || !!busy}
                   onClick={() => act('settings', { settings: dsChanged }, () => 'Saved. It applies at the next rebuild.')}>Save</Btn>
              <DangerBtn small onConfirm={() => { if (capOk && !busy && !live) void rebuild(); }}>
                {Object.keys(dsChanged).length ? 'Save and rebuild the index' : 'Rebuild the index with this choice'}
              </DangerBtn>
            </div>
            <p className="text-xs text-grayscale-1100">
              The rebuild makes a new serving index, then a new keyword index (a minute or two); searches keep using the
              live ones until each is complete. Vectors are kept for every statement, so switching back needs no embedding.
              The counts are companies, so a company over the limit is left out whole. Cards members saved from companies
              left out show as no longer available until they are back in.
            </p>
          </div>
        )}
      </Section>

      <Section title="Indexes" right={<Btn small onClick={() => res.reload()}>Refresh</Btn>}>
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-grayscale-300 p-3 flex flex-col gap-2">
            <div className="text-sm font-semibold text-grayscale-1300">Keyword index · Typesense</div>
            <p className="text-xs text-grayscale-1100">
              Short searches: typos (“batery”), plurals, British and American spellings, filters, ~20 ms.
              {' '}{ts.collection ? `Live collection ${ts.collection}.` : 'Not built yet.'}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Btn disabled={!!busy || live} onClick={() => act('build', { target: 'typesense', mode: 'upsert' }, () => 'Started, progress below.')}>
                Update in place
              </Btn>
              <DangerBtn onConfirm={() => act('build', { target: 'typesense', mode: 'recreate' }, () => 'Started, progress below.')}>
                Rebuild from scratch
              </DangerBtn>
            </div>
            <p className="text-xs text-grayscale-1100">
              Rebuild fills a new collection and switches searches to it when it is complete (~1 min), so searches never
              see a half-built index. Update in place adds and changes statements and removes ones that left the serving
              index (not more than 2% at once).
            </p>
          </div>
          <div className="rounded-lg border border-grayscale-300 p-3 flex flex-col gap-2">
            <div className="text-sm font-semibold text-grayscale-1300">Semantic index · vectors</div>
            <p className="text-xs text-grayscale-1100">
              One vector per statement (bge-small, the model COSMOS's semantic search uses). Feeds AI search and the cards'
              related statements and similar companies.{vec.built_at ? ` Built ${fmtDate(vec.built_at)}.` : ''}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Btn disabled={!!busy || live} onClick={() => act('build', { target: 'vectors', mode: 'gap' }, () => 'Started, progress below.')}>
                {vecTodo || vec.removed ? `Embed the ${num(vecTodo)} missing` : 'Check for missing'}
              </Btn>
              <DangerBtn onConfirm={() => act('build', { target: 'vectors', mode: 'full' }, () => 'Started, progress below.')}>
                Re-embed everything
              </DangerBtn>
            </div>
            <p className="text-xs text-grayscale-1100">
              Free (runs on this server, no API calls) but CPU-heavy: all statements take about an hour here. Progress is
              saved every 50,000, so a stopped build carries on as “embed the missing”.
            </p>
          </div>
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {job.label && (
          <div className="rounded-lg border border-grayscale-300 p-3 text-xs flex flex-col gap-1.5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-grayscale-1300">{job.label} · {job.running ? 'running' : job.status}</span>
              <span className="text-grayscale-1100">by {job.by}, {fmtDate(job.started_at)}</span>
              {job.running && <Btn small onClick={() => act('stop', {}, () => 'Stopping after the current batch.')}>Stop</Btn>}
            </div>
            {job.running && <Bar done={job.done || 0} total={job.total} />}
            {!job.running && (job.error || job.result) && (
              <div className="text-grayscale-1100">
                {job.error || Object.entries(job.result || {}).map(([k, v]) => `${k.replace(/_/g, ' ')} ${typeof v === 'number' ? num(v) : String(v)}`).join(' · ')}
              </div>
            )}
            {(job.log || []).length > 0 && (
              <pre className="whitespace-pre-wrap text-[11px] text-grayscale-1100 max-h-40 overflow-auto">{(job.log || []).join('\n')}</pre>
            )}
          </div>
        )}
      </Section>

      <Section title="Settings" right={
        <div className="flex gap-2">
          <Btn small disabled={!Object.keys(changed).length} onClick={() => setDraft({ ...d.settings })}>Undo</Btn>
          <Btn small kind="primary" disabled={!Object.keys(changed).length || !!busy}
               onClick={() => act('settings', { settings: changed }, () => 'Saved. Held results were cleared.')}>Save</Btn>
        </div>
      }>
        <div className="grid gap-x-6 gap-y-2 md:grid-cols-2">
          {ORDER.filter(k => d.settings && k in d.settings).map(k => (
            <label key={k} className="flex items-center justify-between gap-3 text-sm text-grayscale-1200 border-b border-grayscale-200 py-1.5">
              <span>
                {LABELS[k] || k}
                {draft && d.defaults && draft[k] !== d.defaults[k] && (
                  <span className="text-xs text-grayscale-1100"> (default {String(CHOICE_LABELS[k]?.[d.defaults[k]] ?? d.defaults[k])})</span>
                )}
              </span>
              {field(k)}
            </label>
          ))}
        </div>
        <p className="text-xs text-grayscale-1100">
          Auto route: two words or fewer is a keyword search; four or more describe a need and go to AI search (the LLM
          scores the candidates, ~10–20 s, about 5 LLM calls); three ask a small model. AI search off keeps every search on
          the keyword route at no LLM cost. Cards: “Both, with a switch” keeps the cards’ “Matches within” switch, starting
          on “similar lists within”; a single choice shows only that set on every card and hides the switch. Both sets stay
          stored, so switching back loses nothing.
          {d.updated_by ? ` Last saved by ${d.updated_by}, ${fmtDate(d.updated_at)}.` : ''}
        </p>
      </Section>

      <Section title="Test search">
        <div className="flex gap-2 flex-wrap items-center">
          <TextInput wide value={tq} onChange={setTq} placeholder="e.g. batery recycling, or a sentence describing a need" />
          <Select value={troute} onChange={setTroute} options={['auto', 'keyword', 'ai']}
                  labels={{ auto: 'Route: auto', keyword: 'Keyword', ai: 'AI (paid)' }} />
          <Btn kind="primary" disabled={!tq.trim() || !!busy} onClick={runTest}>{busy === 'test' ? 'Searching…' : 'Search'}</Btn>
          <Btn small onClick={() => act('clear_cache', {}, x => `Cleared ${num(x.cleared)} held result sets.`)}>
            Clear held results ({num(d.cache?.entries)})
          </Btn>
        </div>
        {test?.error && <Msg kind="error">{test.error}</Msg>}
        {test && !test.error && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-grayscale-1100">
              {[
                `route ${test.search?.route}`, test.search?.engine && `engine ${test.search.engine}`,
                test.search?.classify?.reason, test.search?.held != null && `${num(test.search.held)} held of ${num(test.search.found)}`,
                `${num(test.search?.took_ms)} ms${test.search?.cached ? ' (held results)' : ''}`,
                (test.search?.corrected || []).length > 0 && `corrected ${(test.search.corrected || []).map((c: string[]) => `${c[0]} → ${c[1]}`).join(', ')}`,
                (test.search?.applied_from_query || []).length > 0 && `from the words: ${(test.search.applied_from_query || []).map((a: any) => a.value).join(', ')}`,
                test.search?.nonsense?.flagged && 'flagged as not a topic',
                test.search?.llm_calls && `${test.search.llm_calls} LLM calls`,
              ].filter(Boolean).join(' · ')}
            </p>
            {(test.search?.notes || []).length > 0 && <p className="text-xs text-red-700">{test.search.notes.join(' · ')}</p>}
            <DataTable rows={(test.problems?.items || []).map((p: any) => ({
              relevance: p.relevance, statement: p.statement, company: p.company?.name, industry: p.industry, country: p.country,
            }))} columns={['relevance', 'statement', 'company', 'industry', 'country']} empty="No results." />
          </div>
        )}
      </Section>

      <Section title="Recent member searches">
        <DataTable rows={(d.searches || []).map((x: any) => ({
          at: x.at, q: x.q, filters: Object.values(x.filters || {}).flat().join(', '), route: x.route, engine: x.engine,
          held: x.held, found: x.found, shown: x.shown, took_ms: x.took_ms, cached: x.cached ? 'yes' : '',
        }))} columns={['at', 'q', 'filters', 'route', 'engine', 'shown', 'held', 'found', 'took_ms', 'cached']}
                   labels={{ at: 'When', q: 'Search', took_ms: 'ms', cached: 'Held' }}
                   searchable csvName="ocean_searches" empty="No searches yet." />
      </Section>

      <Section title="Index runs">
        <DataTable rows={(d.runs || []).map((x: any) => ({
          started_at: x.started_at, mode: x.mode,
          target: x.target === 'typesense' ? 'Keyword' : x.target === 'serving' ? 'Serving + keyword' : 'Semantic',
          status: x.status, by: x.by_email, seconds: x.seconds,
          result: x.error || (x.result ? Object.entries(x.result).filter(([, v]) => typeof v === 'number').map(([k, v]) => `${k} ${num(v)}`).join(' · ') : ''),
        }))} columns={['started_at', 'target', 'mode', 'status', 'by', 'seconds', 'result']}
                   labels={{ started_at: 'Started' }} empty="No builds from this page yet." />
      </Section>
    </div>
  );
};

export default SearchEngine;
